import { supabase } from '../config/supabase';
import { StepContent, getOnboardingSteps } from '../data/onboardingStepsData';

/**
 * Service for managing organization-specific onboarding steps in the database
 */
export const onboardingStepsService = {
  /**
   * Get onboarding steps for an organization
   * Returns custom steps from database if they exist, otherwise returns default steps
   */
  async getOnboardingSteps(orgId: number): Promise<StepContent[]> {
    try {
      // Try to fetch custom steps from database
      const { data, error } = await supabase
        .from('onboarding_steps')
        .select('steps_data')
        .eq('org_id', orgId)
        .single();

      if (error && error.code !== 'PGRST116') {
        // PGRST116 = no rows returned, which is fine - use defaults
        console.error('Error fetching onboarding steps:', error);
        // Fall back to default steps
        return getOnboardingSteps(orgId);
      }

      // If we have custom steps, return them; otherwise return defaults
      if (data && data.steps_data) {
        return data.steps_data as StepContent[];
      }

      return getOnboardingSteps(orgId);
    } catch (error) {
      console.error('Error in getOnboardingSteps:', error);
      // Fall back to default steps on any error
      return getOnboardingSteps(orgId);
    }
  },

  /**
   * Save onboarding steps for an organization
   */
  async saveOnboardingSteps(
    orgId: number,
    steps: StepContent[],
    userId?: string
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('onboarding_steps')
        .upsert({
          org_id: orgId,
          steps_data: steps,
          updated_at: new Date().toISOString(),
          created_by: userId || null,
        }, {
          onConflict: 'org_id'
        });

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error saving onboarding steps:', error);
      throw error;
    }
  },

  /**
   * Check if an organization has custom onboarding steps
   */
  async hasCustomSteps(orgId: number): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('onboarding_steps')
        .select('id')
        .eq('org_id', orgId)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error checking custom steps:', error);
        return false;
      }

      return !!data;
    } catch (error) {
      console.error('Error in hasCustomSteps:', error);
      return false;
    }
  },

  /**
   * Seed the database with existing onboarding steps from onboardingStepsData.ts
   * This should be run ONCE after creating the table to preserve existing data
   * 
   * Usage: Call this method once from the browser console or a one-time script
   * Example: await onboardingStepsService.seedExistingSteps();
   */
  async seedExistingSteps(userId?: string): Promise<{ success: boolean; message: string }> {
    try {
      // Dynamic import to get the actual data
      const onboardingData = await import('../data/onboardingStepsData');
      const org1OnboardingSteps = onboardingData.org1OnboardingSteps;
      const org2OnboardingSteps = onboardingData.org2OnboardingSteps;
      const org3OnboardingSteps = onboardingData.org3OnboardingSteps;
      
      const results: string[] = [];
      
      // Seed org_id = 1
      const hasOrg1 = await this.hasCustomSteps(1);
      if (!hasOrg1) {
        await this.saveOnboardingSteps(1, org1OnboardingSteps, userId);
        results.push('✓ Seeded org_id = 1');
      } else {
        results.push('⊘ org_id = 1 already exists, skipped');
      }

      // Seed org_id = 2
      const hasOrg2 = await this.hasCustomSteps(2);
      if (!hasOrg2) {
        await this.saveOnboardingSteps(2, org2OnboardingSteps, userId);
        results.push('✓ Seeded org_id = 2');
      } else {
        results.push('⊘ org_id = 2 already exists, skipped');
      }

      // Seed org_id = 3
      const hasOrg3 = await this.hasCustomSteps(3);
      if (!hasOrg3) {
        await this.saveOnboardingSteps(3, org3OnboardingSteps, userId);
        results.push('✓ Seeded org_id = 3');
      } else {
        results.push('⊘ org_id = 3 already exists, skipped');
      }

      return {
        success: true,
        message: `Seeding complete:\n${results.join('\n')}`
      };
    } catch (error: any) {
      console.error('Error seeding existing steps:', error);
      return {
        success: false,
        message: `Error seeding steps: ${error.message}`
      };
    }
  },
};

